import { useEffect } from 'react'

import { summarizeObjects } from 'utils/object'
import { useApiGetPenilaianPs } from 'api/kinerja/kepala-sekolah/pengawas-sekolah/penilaian'
import { useApiGetPenilaianPsTabGuru } from 'api/kinerja/kepala-sekolah/guru/pengawas-sekolah/penilaian'

import type {
  TPenilaianFilterStatus,
  TPenilaianTujuanState,
} from 'types/kinerja-ps'
import type { TabFilterRoles } from 'types/kinerja-ks'

type Props = {
  activeTabName: TabFilterRoles
  name?: string
  status?: TPenilaianFilterStatus[]
  state?: TPenilaianTujuanState
  confirmedOnly?: boolean
  onBothFetched?: () => void
}

const usePenetapanPs = ({
  activeTabName,
  name,
  status,
  state = 'PS_TO_DINAS',
  confirmedOnly = false,
  onBothFetched,
}: Props) => {
  // Calculations for Kepala Sekolah
  const {
    isLoading: isListLoadingKs,
    isError: isListErrorKs,
    isSuccess: isListSuccessKs,
    isFetched: isListFetchedKs,
    data: { data: itemsKs, meta: metasKs } = {},
    refetch: refetchListKs,
  } = useApiGetPenilaianPs({
    name,
    status,
    state,
    confirmedOnly,
  })

  // Calculations for Guru
  const {
    isLoading: isListLoadingGuru,
    isError: isListErrorGuru,
    isSuccess: isListSuccessGuru,
    isFetched: isListFetchedGuru,
    data: { data: itemsGuru, meta: metasGuru } = {},
    refetch: refetchListGuru,
  } = useApiGetPenilaianPsTabGuru({
    name,
    status,
    confirmedOnly,
  })

  const isListLoading = isListLoadingKs || isListLoadingGuru
  const isListError = isListErrorKs || isListErrorGuru
  const isListSuccess = isListSuccessKs || isListSuccessGuru
  const isListBothFetched = isListFetchedKs && isListFetchedGuru

  // Items based on active tab
  const items = activeTabName === 'KS' ? itemsKs : itemsGuru
  const itemsKsCount = itemsKs?.length || 0
  const itemsGuruCount = itemsGuru?.length || 0

  // Total items
  const totalItems = itemsKsCount + itemsGuruCount

  const metas = {
    ...metasKs,
    counts: summarizeObjects(metasKs?.counts, metasGuru?.counts),
  }

  useEffect(() => {
    if (isListBothFetched && !isListLoading) onBothFetched?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListBothFetched])

  return {
    // Kepala Sekolah
    isListLoadingKs,
    isListErrorKs,
    isListSuccessKs,
    itemsKs,
    itemsCountKs: itemsKsCount,
    metasKs,
    refetchListKs,

    // Guru
    isListLoadingGuru,
    isListErrorGuru,
    isListSuccessGuru,
    itemsGuru,
    itemsCountGuru: itemsGuruCount,
    metasGuru,
    refetchListGuru,

    // Combined
    isListLoading,
    isListError,
    isListSuccess,
    isListBothFetched,
    metas,
    totalItems,
    hasItems: totalItems > 0,

    // Relative to active tab
    items,
    relativeItems: items, // Serve as an alias for items
    isRelativeLoading:
      activeTabName === 'KS' ? isListLoadingKs : isListLoadingGuru,
    isRelativeError: activeTabName === 'KS' ? isListErrorKs : isListErrorGuru,
    isRelativeSuccess:
      activeTabName === 'KS' ? isListSuccessKs : isListSuccessGuru,
  }
}

export default usePenetapanPs
